import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useUserStore } from './../../stores/user-store';
import { isAdmin, isAdminOrMaintainer, isOperator } from './../../utils/permissions';
import { MobileNavDrawer } from './mobile-drawer';
import { type NavTab, renderTabLink } from './nav-tab';
import { UserMenuDropdown } from './user-menu-dropdown';

const fullTabs: NavTab[] = [
  { to: 'print-jobs', label: 'navPrintJobs' },
  { to: 'printers', label: 'navPrinters' },
  { to: 'labels', label: 'navLabels' },
  { to: 'graphics', label: 'navGraphics' },
  { to: 'form', label: 'navForm' },
];

const adminOnlyTabs: NavTab[] = [{ to: 'locations', label: 'navLocations' }];

const adminOrMaintainerTabs: NavTab[] = [{ to: 'servers', label: 'navServers' }];

// Operator-only users (no `app.{app}` user role) get a stripped-down nav.
// They only need to see the operator-facing surfaces.
const operatorTabs: NavTab[] = [
  { to: 'print-jobs', label: 'navPrintJobs' },
  { to: 'printers', label: 'navPrinters' },
  { to: 'form', label: 'navForm' },
];

export const Navbar = () => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { instance, inProgress } = useMsal();
  const location = useLocation().pathname.split('/').filter(Boolean);
  const [selectedTab, setSelectedTab] = useState<string>(location[0] || '');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const tabsArr = useMemo<NavTab[]>(() => (isOperator(user) ? operatorTabs : fullTabs), [user]);

  const handleSignOut = async () => {
    if (inProgress !== InteractionStatus.None) {
      console.warn('MSAL interaction in progress:', inProgress);
      return;
    }
    try {
      const account = instance.getActiveAccount();
      console.log('Signing out account:', account?.username);
      await instance.logoutRedirect({
        account,
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Sign-out failed:', error);
    }
  };

  const userIsAdmin = isAdmin(user);
  const userIsAdminOrMaintainer = isAdminOrMaintainer(user);

  useEffect(() => {
    const currentPath = location[0] || '';
    if (
      tabsArr.some((tab) => tab.to === currentPath) ||
      (userIsAdmin && adminOnlyTabs.some((tab) => tab.to === currentPath)) ||
      (userIsAdminOrMaintainer && adminOrMaintainerTabs.some((tab) => tab.to === currentPath)) ||
      (!isOperator(user) && currentPath === 'operator-access') ||
      currentPath === 'form-print'
    ) {
      setSelectedTab(
        currentPath === 'operator-access' || currentPath === 'form-print' ? 'form' : currentPath
      );
    } else {
      setSelectedTab('');
    }
  }, [location, user, userIsAdmin, userIsAdminOrMaintainer, tabsArr]);

  return (
    <nav
      className="relative bg-core-blue-900 border-b-10 border-core-slate-100 px-300 py-200 tablet:px-400"
      style={{ paddingTop: 'calc(env(safe-area-inset-top) + 8px)' }}
    >
      <div className="relative flex flex-row items-center gap-150 xl:gap-200">
        {/* Brand */}
        <div className="flex-1 min-w-0 xl:flex-none xl:mr-300">
          <Link
            to="/"
            className="flex items-center space-x-200 xl:space-x-300 hover:opacity-80 transition-opacity duration-200 w-fit"
          >
            <img src="/lego-logo-180.png" alt="LEGO Logo" className="h-400 xl:h-500 w-auto" />
            <div className="flex flex-col min-w-0">
              <h1 className="text-heading-md xl:text-heading-lg text-core-white font-brand truncate">
                {t('navbar.display.appTitle')}
              </h1>
              <p className="hidden sm:block text-body-sm text-core-white font-brand truncate">
                {t('navbar.display.appSubtitle')}
              </p>
            </div>
          </Link>
        </div>

        {/* Desktop nav tabs (xl+) */}
        <div className="hidden xl:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 items-center space-x-200">
          {tabsArr.map((tab) => renderTabLink(tab, t, selectedTab, setSelectedTab))}
          {userIsAdmin &&
            adminOnlyTabs.map((tab) => renderTabLink(tab, t, selectedTab, setSelectedTab))}
          {userIsAdminOrMaintainer &&
            adminOrMaintainerTabs.map((tab) => renderTabLink(tab, t, selectedTab, setSelectedTab))}
        </div>

        {/* Desktop right-side actions (xl+):
            hamburger menu holds language + docs + role + sign out */}
        <div className="hidden xl:flex items-center ml-auto self-center">
          <UserMenuDropdown onSignOut={handleSignOut} />
        </div>

        {/* Mobile hamburger (<xl) — opens the nav drawer */}
        <button
          id="navbar-mobile-burger"
          type="button"
          aria-label="Toggle navigation menu"
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen((v) => !v)}
          className="xl:hidden ml-auto p-200 text-core-white rounded-100 hover:bg-core-white/10"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      </div>

      <MobileNavDrawer
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        tabs={tabsArr}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        adminOnlyTabs={userIsAdmin ? adminOnlyTabs : []}
        adminOrMaintainerTabs={userIsAdminOrMaintainer ? adminOrMaintainerTabs : []}
        onSignOut={handleSignOut}
      />
    </nav>
  );
};
